/**
 * File GSTR-1 on the portal — the whole flow, in one panel.
 * @file src/web/features/admin/components/FileGstr1Panel.tsx
 *
 * Two OTPs bracket this, both to the client's own phone, because GSTN
 * requires a person to authorise a return. The CA rings the client once, and
 * everything between the two codes runs without them.
 *
 * The steps are shown as a list rather than a hidden wizard so a CA can see
 * where a filing stopped and resume, which is what actually happens when a
 * client does not pick up. Save, proceed and summary are re-runnable; the
 * final submit is not, and is styled and confirmed accordingly.
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Check, AlertTriangle, Smartphone, ShieldCheck, Send } from 'lucide-react';
import { adminFetchJson } from '../lib/admin-api';

interface SessionInfo {
  ok: boolean; error?: string;
  configured?: boolean; testEnvironment?: boolean;
  gstin?: string; portalUsername?: string | null; usernameMissing?: boolean;
  session?: { openedAt: string; expiresAt: string; minutesLeft: number } | null;
}

type StepKey = 'save' | 'proceed' | 'summary' | 'evc';
const STEPS: { key: StepKey; label: string; detail: string }[] = [
  { key: 'save', label: 'Upload return', detail: 'Sends the GSTR-1 we built to the portal. Re-runnable — saving twice overwrites.' },
  { key: 'proceed', label: 'Proceed to file', detail: 'Portal validates and computes its own summary.' },
  { key: 'summary', label: 'Fetch portal summary', detail: "GSTN's own figures and checksums — confirm against these, not ours." },
  { key: 'evc', label: 'Send EVC OTP', detail: 'Second OTP to the client, for the signature.' },
];

export function FileGstr1Panel({ entityId, gstRegistrationId, period, filingId }: {
  entityId: string | null; gstRegistrationId?: string | null; period: string; filingId?: string | null;
}) {
  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [evcOtp, setEvcOtp] = useState('');
  const [done, setDone] = useState<Partial<Record<StepKey, boolean>>>({});
  const [summary, setSummary] = useState<unknown>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [arn, setArn] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const q = { entityId: entityId ?? undefined, gstRegistrationId: gstRegistrationId ?? undefined, period };

  const load = useCallback(() => {
    adminFetchJson<SessionInfo>('/api/gst/portal/session', { query: q })
      .then(setInfo)
      .catch((e) => setInfo({ ok: false, error: e instanceof Error ? e.message : 'Failed' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, gstRegistrationId, period]);
  useEffect(load, [load]);

  async function run(name: string, fn: () => Promise<any>) {
    setBusy(name); setMsg(null);
    try {
      const r = await fn();
      if (r?.ok === false) { setMsg({ tone: 'err', text: r.error ?? 'Failed' }); return null; }
      return r;
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message.replace(/^\d+\s/, '') : 'Failed' });
      return null;
    } finally { setBusy(null); }
  }

  const sendOtp = () => run('otp', async () => {
    const r = await adminFetchJson<any>('/api/gst/portal/otp', { method: 'POST', query: q });
    if (r?.ok !== false) setMsg({ tone: 'ok', text: r.message ?? 'OTP sent to the client.' });
    return r;
  });

  const verify = () => run('verify', async () => {
    const r = await adminFetchJson<any>('/api/gst/portal/verify', { method: 'POST', query: q, body: JSON.stringify({ otp }) });
    if (r?.ok !== false) { setOtp(''); setMsg({ tone: 'ok', text: 'Session open for 6 hours.' }); load(); }
    return r;
  });

  const step = (k: StepKey) => run(k, async () => {
    const r = await adminFetchJson<any>(`/api/gst/file/gstr1/${k}`, { method: 'POST', query: q });
    if (r?.ok !== false) {
      setDone((d) => ({ ...d, [k]: true }));
      if (k === 'summary') setSummary(r.data);
      if (k === 'evc') setMsg({ tone: 'ok', text: 'EVC OTP sent to the client.' });
    }
    return r;
  });

  const submit = () => run('submit', async () => {
    const r = await adminFetchJson<any>('/api/gst/file/gstr1/submit', {
      method: 'POST', query: q,
      body: JSON.stringify({ otp: evcOtp, summary, filingId: filingId ?? null }),
    });
    if (r?.ok !== false) { setArn(r.arn); setConfirming(false); setEvcOtp(''); }
    return r;
  });

  if (!info) return <div className="rounded-2xl border border-ink-200 bg-white px-5 py-4 text-sm text-ink-500">Checking portal session…</div>;

  if (info.configured === false) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-ink-50/60 px-5 py-4 text-sm text-ink-600">
        No GSP credentials configured. Add <code className="font-mono text-xs">SANDBOX_GST_API_KEY</code> and{' '}
        <code className="font-mono text-xs">SANDBOX_GST_API_SECRET</code> to file from here. You can still download the
        JSON above and upload it on the portal.
      </div>
    );
  }

  /* On the fixture host the walk cannot be completed: GSTN never sends the
     taxpayer OTP, so every step past "OTP sent" dead-ends. A reviewer handed
     this screen will work through it — they file returns for a living — and
     conclude the feature is broken. Show it as plainly inactive instead. */
  if (info.testEnvironment) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-ink-50/60 px-5 py-4 text-sm text-ink-600">
        <p className="font-medium text-ink-800">Portal filing is off in this build</p>
        <p className="mt-1 text-xs text-ink-500">
          GSTIN {info.gstin} · period {period}. This build is not connected to the GST portal — nothing can be
          filed from here and no OTP is sent. The figures above are computed from the books and are unaffected.
        </p>
      </div>
    );
  }

  const live = !!info.session;

  return (
    <div className="space-y-4 rounded-2xl border border-ink-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-ink-900">File on the GST portal</h3>
          <p className="text-xs text-ink-500">GSTIN {info.gstin} · period {period}</p>
        </div>
        {live && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-ok-200 bg-ok-50 px-3 py-1 text-[11px] font-medium text-ok-700">
            <ShieldCheck className="h-3.5 w-3.5" /> Session open · {info.session!.minutesLeft} min left
          </span>
        )}
      </div>

      {info.usernameMissing && (
        <div className="flex items-start gap-2 rounded-xl border border-danger-200 bg-danger-50/70 px-4 py-2.5 text-xs text-danger-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          No GST-portal username saved for this GSTIN. Add it in Masters → Entity → Statutory registrations — the OTP call needs it.
        </div>
      )}

      {msg && (
        <div className={`rounded-xl border px-4 py-2.5 text-xs ${msg.tone === 'ok' ? 'border-ok-200 bg-ok-50 text-ok-700' : 'border-danger-200 bg-danger-50 text-danger-700'}`}>
          {msg.text}
        </div>
      )}

      {arn ? (
        <div className="rounded-xl border border-ok-200 bg-ok-50 px-4 py-4 text-sm text-ok-800">
          <p className="flex items-center gap-2 font-semibold"><Check className="h-4 w-4" /> Filed.</p>
          <p className="mt-1">ARN <span className="font-mono">{arn}</span> — recorded against this period.</p>
        </div>
      ) : (
        <>
          {/* ── 1. taxpayer session ── */}
          <div className="rounded-xl border border-ink-200 p-4">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">
              <Smartphone className="h-3.5 w-3.5" /> Step 1 — client&rsquo;s OTP
            </p>
            {live ? (
              <p className="mt-2 text-xs text-ink-500">Session already open. No need to disturb the client again until it expires.</p>
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button onClick={sendOtp} disabled={!!busy || info.usernameMissing} className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-4 py-2 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50">
                  {busy === 'otp' && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Send OTP to client
                </button>
                <input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="OTP from client"
                  className="w-40 rounded-lg border border-ink-300 px-3 py-2 text-sm tabular-nums focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter" />
                <button onClick={verify} disabled={!!busy || !otp.trim()} className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
                  {busy === 'verify' && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Open session
                </button>
              </div>
            )}
          </div>

          {/* ── 2-5. the automatic part ── */}
          <div className="rounded-xl border border-ink-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">Steps 2–5 — no client needed</p>
            <ul className="mt-3 space-y-2">
              {STEPS.map((s) => (
                <li key={s.key} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-ink-800">{s.label}</span>
                    <span className="block text-[11px] text-ink-400">{s.detail}</span>
                  </div>
                  <button onClick={() => step(s.key)} disabled={!live || !!busy}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-40">
                    {busy === s.key && <Loader2 className="h-3 w-3 animate-spin" />}
                    {done[s.key] ? <><Check className="h-3 w-3 text-ok-600" /> Done</> : 'Run'}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ── 6. the irreversible one ── */}
          <div className="rounded-xl border border-danger-200 bg-danger-50/40 p-4">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-danger-700">
              <Send className="h-3.5 w-3.5" /> Step 6 — file
            </p>
            <p className="mt-1 text-[11px] text-danger-700">
              Irreversible. Once GSTN accepts this, the return can only be corrected as an amendment in a later period.
            </p>
            {!confirming ? (
              <button onClick={() => setConfirming(true)} disabled={!live || !done.summary || !done.evc}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-danger-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-40">
                File GSTR-1
              </button>
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input value={evcOtp} onChange={(e) => setEvcOtp(e.target.value)} placeholder="EVC OTP"
                  className="w-36 rounded-lg border border-danger-300 px-3 py-2 text-sm tabular-nums focus:outline-none" />
                <button onClick={submit} disabled={!!busy || !evcOtp.trim()}
                  className="inline-flex items-center gap-1.5 rounded-full bg-danger-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
                  {busy === 'submit' && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Confirm &amp; file
                </button>
                <button onClick={() => setConfirming(false)} className="rounded-full px-3 py-2 text-xs font-medium text-ink-600 hover:text-ink-900">Cancel</button>
              </div>
            )}
            {!done.summary && <p className="mt-2 text-[11px] text-ink-400">Fetch the portal summary first — filing uses GSTN&rsquo;s figures, not ours.</p>}
          </div>
        </>
      )}
    </div>
  );
}
